import { useState, useEffect, useRef } from 'react'
import { useForecast } from '../../hooks/useForecast'
import { useMe } from '../../hooks/useMe'
import { ApiClient } from '../../api'
import { C, F } from './theme'
import { Card, SectionHead, Btn, Icon, Head, LockedCard, gbp } from './prims'
import type { ForecastRevenueLine, ForecastCostLine } from '../../types'

// EM Sprint 1.5 · Costs & revenue — flexible line editors for the forecast P&L.
// Ticket revenue stays att×yield on the Model tab; here the planner builds the
// rest: revenue lines (gate walk-ups + on-the-day departments + ad-hoc) and cost
// lines (imported from a comparable event's budget, or manual). All money is
// integer PENCE; edited in pounds and converted at the input edge. Debounced
// save mirrors the Model tab. deriveModel() sums these live into the headline.

function newKey(): string {
  try { return crypto.randomUUID() } catch { return 'ln_' + Date.now().toString(36) + Math.round(Math.random() * 1e6).toString(36) }
}

const SOURCE_LABEL: Record<string, string> = { manual: 'Manual', budget: 'Budget', merac: 'MERAC', adhoc: 'Ad-hoc', contract: 'Contract' }

function PoundInput({ pence, onChange }: { pence: number; onChange: (pence: number) => void }) {
  // Edit in pounds (2dp) → store integer pence.
  const [text, setText] = useState<string>(pence ? (pence / 100).toString() : '')
  useEffect(() => { setText(pence ? (pence / 100).toString() : '') }, [pence])
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span style={{ fontFamily: F.mono, fontSize: 12.5, color: C.inkFaint }}>£</span>
      <input
        value={text}
        inputMode="decimal"
        onChange={(e) => { const v = e.target.value; setText(v); const n = parseFloat(v); onChange(Number.isFinite(n) && n > 0 ? Math.round(n * 100) : 0) }}
        style={{ width: 96, padding: '6px 8px', border: `1px solid ${C.line}`, borderRadius: 7, fontFamily: F.mono, fontSize: 12.5, color: C.ink, textAlign: 'right' }}
      />
    </div>
  )
}

export function CostsRevenueTab({ eventId }: { eventId: number }) {
  const { fc, save } = useForecast(eventId)
  const { data: me } = useMe()

  const [rev, setRev] = useState<ForecastRevenueLine[]>([])
  const [costs, setCosts] = useState<ForecastCostLine[]>([])
  const [importFrom, setImportFrom] = useState<number | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const seeded = useRef<number | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!fc) return
    if (seeded.current === eventId) return
    seeded.current = eventId
    setRev(fc.revenueStreams)
    setCosts(fc.costLines)
  }, [fc, eventId])
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

  if (!me?.hasFinancial) return <LockedCard label="Costs & revenue need financial access" />
  if (!fc) return null

  const comparables = fc.baseline
  const targetId = importFrom ?? comparables[0]?.sourceEventId ?? null

  const persist = (r: ForecastRevenueLine[], c: ForecastCostLine[]) => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => { void save({ revenueStreams: r, costLines: c }).catch(() => { /* never throw on API error */ }) }, 500)
  }
  const setRevenue = (next: ForecastRevenueLine[]) => { setRev(next); persist(next, costs) }
  const setCostLines = (next: ForecastCostLine[]) => { setCosts(next); persist(rev, next) }

  const flash = (m: string) => { setNote(m); setTimeout(() => setNote(null), 4000) }

  const importCosts = async () => {
    if (!targetId) return
    setBusy('costs')
    try {
      const bp = await ApiClient.eventBudget(targetId)
      const add: ForecastCostLine[] = bp.lines
        .filter((l) => l.category.kind === 'cost' && (l.plannedAmount || l.actualAmount))
        .map((l) => ({ key: newKey(), label: (l.description || l.category.name).slice(0, 80), pence: Math.round((l.plannedAmount || l.actualAmount || 0) * 100), source: 'budget' }))
      if (add.length === 0) flash('No cost lines found on that event’s budget.')
      else { setCostLines([...costs, ...add]); flash(`Imported ${add.length} cost line${add.length === 1 ? '' : 's'} from the budget.`) }
    } catch { flash('Couldn’t read that event’s budget.') } finally { setBusy(null) }
  }

  const importRevenue = async () => {
    if (!targetId) return
    setBusy('rev')
    try {
      const inc = await ApiClient.eventIncome(targetId)
      const add: ForecastRevenueLine[] = []
      if (inc.inhouse?.pence > 0) add.push({ key: newKey(), label: 'In-house / walk-up', pence: inc.inhouse.pence, source: 'merac', scales: true })
      for (const cat of inc.onTheDay?.byCategory ?? []) if (cat.pence > 0) add.push({ key: newKey(), label: cat.label, pence: cat.pence, source: 'merac', scales: true })
      if (add.length === 0) flash('No on-the-day revenue found for that event.')
      else { setRevenue([...rev, ...add]); flash(`Imported ${add.length} revenue line${add.length === 1 ? '' : 's'} (in-house + departments).`) }
    } catch { flash('Couldn’t read that event’s income.') } finally { setBusy(null) }
  }

  const importPicker = comparables.length > 0 && (
    <select
      value={targetId ?? ''}
      onChange={(e) => setImportFrom(Number(e.target.value) || null)}
      style={{ padding: '6px 8px', border: `1px solid ${C.line}`, borderRadius: 7, fontFamily: F.body, fontSize: 12, color: C.ink, background: C.card, maxWidth: 200 }}
    >
      {comparables.map((b) => <option key={b.sourceEventId} value={b.sourceEventId}>{b.name}</option>)}
    </select>
  )

  const revTotal = rev.reduce((s, l) => s + (l.pence || 0), 0)
  const costTotal = costs.reduce((s, l) => s + (l.pence || 0), 0)

  return (
    <div>
      <Head eyebrow="Money · lines" title="Costs & revenue"
        sub="Ticket revenue is modelled from attendance × yield on the Model tab. Build the rest of the P&L here — import a comparable event’s figures, or add lines manually." />

      {note && (
        <div style={{ marginBottom: 14, padding: '9px 13px', borderRadius: 9, background: C.greenBg, color: C.green, fontFamily: F.body, fontSize: 12.5, fontWeight: 700, border: `1px solid ${C.greenSoft}` }}>{note}</div>
      )}

      {comparables.length === 0 && (
        <Card style={{ marginBottom: 16, borderColor: `${C.brass}44`, background: C.brassBg }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <Icon name="info" size={17} color={C.brass} />
            <div style={{ fontFamily: F.body, fontSize: 12.5, color: C.inkSoft }}>Link a comparable event in <strong>Baseline &amp; data</strong> to import its real budget and income. You can still add lines manually below.</div>
          </div>
        </Card>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(340px,1fr))', gap: 16 }}>
        {/* Revenue */}
        <Card>
          <SectionHead icon="trend" label="Other revenue" right={
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {importPicker}
              <Btn variant="soft" small icon="download" disabled={!targetId || busy === 'rev'} onClick={importRevenue}>{busy === 'rev' ? 'Importing…' : 'Import income'}</Btn>
            </div>
          } />
          {rev.length === 0 && <Empty label="No revenue lines yet — import gate + department income, or add ad-hoc lines." />}
          {rev.map((l, i) => (
            <LineRow key={l.key} label={l.label} source={l.source} pence={l.pence}
              onLabel={(v) => setRevenue(rev.map((x, j) => j === i ? { ...x, label: v } : x))}
              onPence={(v) => setRevenue(rev.map((x, j) => j === i ? { ...x, pence: v } : x))}
              onDelete={() => setRevenue(rev.filter((_, j) => j !== i))}
              scales={l.scales} onScales={(v) => setRevenue(rev.map((x, j) => j === i ? { ...x, scales: v } : x))} />
          ))}
          <AddRow onClick={() => setRevenue([...rev, { key: newKey(), label: '', pence: 0, source: 'adhoc', scales: false }])} label="Add revenue line" />
          <TotalRow label="Revenue lines total" pence={revTotal} />
        </Card>

        {/* Costs */}
        <Card>
          <SectionHead icon="pound" label="Costs" right={
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {importPicker}
              <Btn variant="soft" small icon="download" disabled={!targetId || busy === 'costs'} onClick={importCosts}>{busy === 'costs' ? 'Importing…' : 'Import budget'}</Btn>
            </div>
          } />
          {costs.length === 0 && <Empty label="No cost lines yet — import a comparable event’s budget, or add lines manually." />}
          {costs.map((l, i) => (
            <LineRow key={l.key} label={l.label} source={l.source} pence={l.pence}
              onLabel={(v) => setCostLines(costs.map((x, j) => j === i ? { ...x, label: v } : x))}
              onPence={(v) => setCostLines(costs.map((x, j) => j === i ? { ...x, pence: v } : x))}
              onDelete={() => setCostLines(costs.filter((_, j) => j !== i))} />
          ))}
          <AddRow onClick={() => setCostLines([...costs, { key: newKey(), label: '', pence: 0, source: 'manual' }])} label="Add cost line" />
          <TotalRow label="Base costs total" pence={costTotal} />
        </Card>
      </div>

      <div style={{ marginTop: 14, fontFamily: F.body, fontSize: 11.5, color: C.inkFaint }}>
        Imported figures are a starting point — edit or remove them. “Scales” revenue grows with the attendance slider; fixed lines don’t. Cost lines feed the Model P&L and the published net-incremental.
      </div>
    </div>
  )
}

function LineRow({ label, source, pence, scales, onLabel, onPence, onDelete, onScales }: {
  label: string; source: string; pence: number; scales?: boolean
  onLabel: (v: string) => void; onPence: (v: number) => void; onDelete: () => void; onScales?: (v: boolean) => void
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: `1px solid ${C.lineSoft}` }}>
      <input value={label} placeholder="Line description" onChange={(e) => onLabel(e.target.value)}
        style={{ flex: 1, minWidth: 0, padding: '6px 8px', border: `1px solid ${C.line}`, borderRadius: 7, fontFamily: F.body, fontSize: 12.5, color: C.ink }} />
      <span title={SOURCE_LABEL[source] ?? source} style={{ fontFamily: F.body, fontSize: 9.5, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: C.inkFaint, minWidth: 44, textAlign: 'center' }}>{SOURCE_LABEL[source] ?? source}</span>
      {onScales && (
        <label title="Scales with attendance" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: F.body, fontSize: 10.5, fontWeight: 700, color: scales ? C.green : C.inkFaint, cursor: 'pointer' }}>
          <input type="checkbox" checked={scales !== false} onChange={(e) => onScales(e.target.checked)} style={{ accentColor: C.green }} />scales
        </label>
      )}
      <PoundInput pence={pence} onChange={onPence} />
      <button onClick={onDelete} title="Remove" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: C.inkFaint, display: 'flex', padding: 4 }}><Icon name="x" size={15} /></button>
    </div>
  )
}

function AddRow({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button onClick={onClick} style={{ marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 6, border: `1px dashed ${C.line}`, background: 'transparent', borderRadius: 8, padding: '7px 11px', cursor: 'pointer', fontFamily: F.body, fontSize: 12, fontWeight: 700, color: C.green }}>
      <Icon name="plus" size={13} />{label}
    </button>
  )
}

function TotalRow({ label, pence }: { label: string; pence: number }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 12, paddingTop: 9, borderTop: `1px solid ${C.lineSoft}` }}>
      <span style={{ fontFamily: F.body, fontSize: 12, fontWeight: 800, color: C.ink }}>{label}</span>
      <span style={{ fontFamily: F.mono, fontSize: 13.5, fontWeight: 800, color: C.ink }}>{gbp(pence)}</span>
    </div>
  )
}

function Empty({ label }: { label: string }) {
  return <div style={{ fontFamily: F.body, fontSize: 12.5, color: C.inkFaint, padding: '10px 0' }}>{label}</div>
}
